import { useMediaQuery } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

function ChevronLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Responsive rows-per-page control:
 * - Fine pointer (desktop): compact native <select>
 * - Touch / coarse pointer: MUI Select with larger menu items
 */
function RowsPerPageControl({ value, options, onChange }) {
  const isFinePointer = useMediaQuery("(pointer: fine)", { defaultMatches: true, noSsr: true });
  const isNarrow = useMediaQuery("(max-width: 640px)", { defaultMatches: false, noSsr: true });
  const useTouchSelect = !isFinePointer || isNarrow;

  if (!useTouchSelect) {
    return (
      <select
        className="meetai-table-pagination__native-select"
        aria-label="Rows per page"
        value={value}
        onChange={(e) => onChange?.(e)}
      >
        {options.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    );
  }

  return (
    <FormControl size="small" variant="outlined" className="meetai-table-pagination__touch-select">
      <Select
        value={value}
        onChange={(e) => {
          onChange?.({ target: { value: String(e.target.value) } });
        }}
        inputProps={{ "aria-label": "Rows per page" }}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: {
            className: "meetai-table-pagination__menu",
            sx: {
              maxHeight: "min(50vh, 320px)",
              borderRadius: "10px",
              border: "1px solid #DCE6EF",
              boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
            },
          },
        }}
        sx={{
          minWidth: 64,
          height: 32,
          fontSize: 13,
          fontWeight: 600,
          color: "#0B2A4A",
          fontFamily: "inherit",
          background: "#FFFFFF",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DCE6EF" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(8,117,225,0.35)" },
          "& .MuiSelect-select": { py: "4px", px: "10px" },
        }}
      >
        {options.map((n) => (
          <MenuItem key={n} value={n} sx={{ fontSize: 14, minHeight: 44, fontFamily: "inherit" }}>
            {n}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/**
 * AVAADA meeting-list table footer.
 * Compact control group packed to the bottom-RIGHT.
 *
 * Layout:  Rows per page: 5  |  1–4 of 4  |  ‹  ›
 */
export default function MeetAiTablePagination({
  count = 0,
  page = 0,
  rowsPerPage = 5,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  labelRowsPerPage = "Rows per page:",
  labelDisplayedRows,
  className = "",
  showWhenEmpty = false,
}) {
  const size = Math.max(1, Number(rowsPerPage) || 5);
  const total = Math.max(0, Number(count) || 0);
  if (total <= 0 && !showWhenEmpty) return null;

  const maxPage = Math.max(0, Math.ceil(total / size) - 1);
  const safePage = Math.min(Math.max(0, Number(page) || 0), maxPage);
  const options =
    Array.isArray(rowsPerPageOptions) && rowsPerPageOptions.length > 0
      ? rowsPerPageOptions
      : DEFAULT_ROWS_PER_PAGE_OPTIONS;

  const from = total === 0 ? 0 : safePage * size + 1;
  const to = Math.min(total, (safePage + 1) * size);
  const rangeLabel = labelDisplayedRows
    ? labelDisplayedRows({ from, to, count: total, page: safePage })
    : `${from}–${to} of ${total}`;

  return (
    <div className={`meetai-table-pagination ${className}`.trim()} role="navigation" aria-label="Table pagination">
      <div className="meetai-table-pagination__toolbar">
        <span className="meetai-table-pagination__label">{labelRowsPerPage}</span>
        <RowsPerPageControl
          value={size}
          options={options}
          onChange={(e) => onRowsPerPageChange?.(e)}
        />
        <span className="meetai-table-pagination__range" aria-live="polite">
          {rangeLabel}
        </span>
        <div className="meetai-table-pagination__actions">
          <IconButton
            size="small"
            aria-label="Go to previous page"
            disabled={safePage <= 0}
            onClick={(e) => onPageChange?.(e, safePage - 1)}
            className="meetai-table-pagination__arrow"
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Go to next page"
            disabled={safePage >= maxPage}
            onClick={(e) => onPageChange?.(e, safePage + 1)}
            className="meetai-table-pagination__arrow"
          >
            <ChevronRightIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
